import { motion } from "framer-motion";
import { Avatar } from "@/components/User/Avatar";
import { Button } from "@/components/ui/button";
import { Edit } from "lucide-react";
import { BadgeGrid } from "./BadgeGrid";
import { ShareProfileButton } from "./ShareProfileButton";
import { CreatorTierBadge, CreatorTier } from "./CreatorTierBadge";
import { cn } from "@/lib/utils";

interface BadgeType {
  id: string;
  name: string;
  description: string;
  earned_at: string;
}

interface ProfileHeaderProps {
  userId: string;
  username: string;
  avatarId: number | null;
  bio?: string;
  tagline?: string;
  promptCount: number;
  isOwnProfile: boolean;
  badges?: BadgeType[];
  creatorTier?: CreatorTier;
  onEditProfile?: () => void;
}

export function ProfileHeader({ 
  userId, 
  username, 
  avatarId, 
  bio, 
  tagline,
  promptCount,
  isOwnProfile,
  badges = [],
  creatorTier = "emerging",
  onEditProfile
}: ProfileHeaderProps) {
  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="relative bg-card/50 backdrop-blur-sm rounded-2xl p-6 sm:p-8 border border-border/50"
    >
      {/* Main Content */}
      <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
        {/* Avatar */}
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="relative"
        >
          <Avatar userId={userId} avatarId={avatarId} size="lg" />
          {/* Tier indicator ring for top creators */}
          {creatorTier !== "emerging" && (
            <div className={cn(
              "absolute -inset-1 rounded-full -z-10",
              creatorTier === "top1" && "bg-gradient-to-r from-amber-400/30 to-yellow-400/30 blur-sm",
              creatorTier === "top5" && "bg-gradient-to-r from-purple-400/30 to-fuchsia-400/30 blur-sm",
              creatorTier === "top15" && "bg-gradient-to-r from-cyan-400/30 to-blue-400/30 blur-sm"
            )} />
          )}
        </motion.div>
        
        {/* Info */}
        <div className="flex-1 min-w-0 text-center sm:text-left space-y-3">
          {/* Name and Handle */}
          <div className="space-y-1">
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground break-words">{username}</h1>
            <p className="text-muted-foreground text-sm break-all">@{(username || 'anonymous').toLowerCase().replace(/\s+/g, '')}</p>
          </div>

          {/* Tagline - Primary Identity Element */}
          {tagline ? (
            <p className="text-base sm:text-xl font-medium bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent break-words line-clamp-3">
              {tagline}
            </p>
          ) : bio ? (
            <p className="text-base sm:text-xl font-medium bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent break-words line-clamp-2">
              {bio}
            </p>
          ) : null}

          {/* Bio - Secondary */}
          {tagline && bio && (
            <p className="text-muted-foreground text-sm line-clamp-2">
              {bio}
            </p>
          )}

          {/* Creator Tier Badge - Mobile */}
          <div className="sm:hidden flex justify-center">
            <CreatorTierBadge tier={creatorTier} />
          </div>
        </div>
        
        {/* Actions */}
        <div className="flex items-center gap-2 sm:flex-col sm:items-end">
          {isOwnProfile ? (
            <Button
              onClick={onEditProfile}
              variant="outline"
              size="sm"
              className="gap-2"
            >
              <Edit className="w-4 h-4" />
              <span>Edit Profile</span>
            </Button>
          ) : null}
          
          <ShareProfileButton 
            userId={userId} 
            username={username}
            variant="outline"
            size="sm"
          />

          {/* Creator Tier Badge - Desktop */}
          <div className="hidden sm:block mt-2">
            <CreatorTierBadge tier={creatorTier} />
          </div>
        </div>
      </div>
      
      {/* Badges Section */}
      {badges.length > 0 && (
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="mt-6 pt-6 border-t border-border/50"
        >
          <BadgeGrid badges={badges} />
        </motion.div>
      )}
    </motion.div>
  );
}
